/**
 * useDataClassifications Hook
 * PROJ-042 - Data Classification & Canvas Enhancement
 */

import { useState, useEffect, useCallback } from 'react';
import { getDataClassifications } from '@/lib/dataClassificationApi';
import type { DataClassification } from '@/types/data-classification';

interface UseDataClassificationsReturn {
  classifications: DataClassification[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useDataClassifications(): UseDataClassificationsReturn {
  const [classifications, setClassifications] = useState<DataClassification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async (ignoreResult?: { current: boolean }) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDataClassifications();
      if (ignoreResult?.current) return;
      setClassifications(data);
    } catch (err) {
      if (ignoreResult?.current) return;
      setError(err instanceof Error ? err : new Error('Failed to fetch classifications'));
    } finally {
      if (!ignoreResult?.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ignoreResult = { current: false };
    fetchData(ignoreResult);
    return () => { ignoreResult.current = true; };
  }, [fetchData]);

  return {
    classifications,
    loading,
    error,
    refetch: () => fetchData()
  };
}
